/**
 * 设备信息工具类
 * 用于生成和管理浏览器端的设备ID和设备型号信息
 */

const DEVICE_ID_KEY = 'im_device_id';
const DEVICE_ID_VERSION = '1.0';

/**
 * 生成唯一设备ID
 * 使用多种因素组合生成唯一标识符
 */
function generateDeviceId() {
  // 获取浏览器指纹信息
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('device-fingerprint', 2, 2);
  const canvasFingerprint = canvas.toDataURL().substring(0, 50);

  // 组合多个因素
  const factors = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    canvasFingerprint,
    navigator.hardwareConcurrency || 0,
    navigator.deviceMemory || 0,
    // 添加随机因子确保唯一性
    Math.random().toString(36).substring(2, 15)
  ].join('|');

  // 生成哈希值
  return hashCode(factors);
}

/**
 * 简单哈希函数
 */
function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // 转换为正数并生成32位十六进制字符串
  const hex = Math.abs(hash).toString(16).padStart(8, '0');
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  
  return `${timestamp}-${hex}-${random}`;
}

/**
 * 获取或创建设备ID
 * 如果本地存储中已有设备ID则返回,否则生成新的并保存
 */
export function getDeviceId() {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    
    // 如果不存在,则生成新的设备ID
    if (!deviceId) {
      deviceId = generateDeviceId();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
      console.log('[DeviceUtils] 生成新设备ID:', deviceId);
    } else {
      console.log('[DeviceUtils] 使用已存在的设备ID:', deviceId);
    }
    
    return deviceId;
  } catch (error) {
    console.error('[DeviceUtils] 获取设备ID失败:', error);
    // 如果localStorage不可用,返回临时ID
    return `temp-${generateDeviceId()}`;
  }
}

/**
 * 获取操作系统信息
 */
function getOSInfo() {
  const userAgent = navigator.userAgent;
  let os = 'Unknown OS';
  
  if (userAgent.includes('Windows NT 10.0')) os = 'Windows 10';
  else if (userAgent.includes('Windows NT 6.3')) os = 'Windows 8.1';
  else if (userAgent.includes('Windows NT 6.2')) os = 'Windows 8';
  else if (userAgent.includes('Windows NT 6.1')) os = 'Windows 7';
  else if (userAgent.includes('Windows')) os = 'Windows';
  else if (userAgent.includes('Mac OS X')) {
    const version = userAgent.match(/Mac OS X ([\d_]+)/);
    os = version ? `macOS ${version[1].replace(/_/g, '.')}` : 'macOS';
  }
  else if (userAgent.includes('Linux')) os = 'Linux';
  else if (userAgent.includes('Android')) {
    const version = userAgent.match(/Android ([\d.]+)/);
    os = version ? `Android ${version[1]}` : 'Android';
  }
  else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    const version = userAgent.match(/OS ([\d_]+)/);
    os = version ? `iOS ${version[1].replace(/_/g, '.')}` : 'iOS';
  }
  
  return os;
}

/**
 * 获取浏览器信息
 */
function getBrowserInfo() {
  const userAgent = navigator.userAgent;
  let browser = 'Unknown Browser';
  
  // 检测各种浏览器
  if (userAgent.includes('Edg/')) {
    const version = userAgent.match(/Edg\/([\d.]+)/);
    browser = version ? `Edge ${version[1]}` : 'Edge';
  } else if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) {
    const version = userAgent.match(/Chrome\/([\d.]+)/);
    browser = version ? `Chrome ${version[1]}` : 'Chrome';
  } else if (userAgent.includes('Firefox/')) {
    const version = userAgent.match(/Firefox\/([\d.]+)/);
    browser = version ? `Firefox ${version[1]}` : 'Firefox';
  } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
    const version = userAgent.match(/Version\/([\d.]+)/);
    browser = version ? `Safari ${version[1]}` : 'Safari';
  } else if (userAgent.includes('Opera/') || userAgent.includes('OPR/')) {
    const version = userAgent.match(/(?:Opera|OPR)\/([\d.]+)/);
    browser = version ? `Opera ${version[1]}` : 'Opera';
  }
  
  return browser;
}

/**
 * 获取设备型号信息
 * 格式: "浏览器 / 操作系统"
 */
export function getDeviceModel() {
  const browser = getBrowserInfo();
  const os = getOSInfo();
  
  return `${browser} / ${os}`;
}

/**
 * 获取完整的设备信息
 */
export function getDeviceInfo() {
  return {
    deviceId: getDeviceId(),
    deviceModel: getDeviceModel(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${screen.width}x${screen.height}`,
    version: DEVICE_ID_VERSION
  };
}

/**
 * 重置设备ID(用于测试或特殊场景)
 */
export function resetDeviceId() {
  try {
    localStorage.removeItem(DEVICE_ID_KEY);
    console.log('[DeviceUtils] 设备ID已重置');
    return true;
  } catch (error) {
    console.error('[DeviceUtils] 重置设备ID失败:', error);
    return false;
  }
}

/**
 * 打印设备信息(调试用)
 */
export function printDeviceInfo() {
  const info = getDeviceInfo();
  console.log('=== 设备信息 ===');
  console.log('设备ID:', info.deviceId);
  console.log('设备型号:', info.deviceModel);
  console.log('User Agent:', info.userAgent);
  console.log('平台:', info.platform);
  console.log('语言:', info.language);
  console.log('屏幕分辨率:', info.screenResolution);
  console.log('===============');
  return info;
}
